import React, {
  PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Box, Checkbox, HStack, Icon, Input, Pressable, Text } from '@rbilabs/universal-components';
import { router } from 'expo-router';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { ActionButtonVariants } from 'components/action-button';
import Dialog from 'components/dialog';
import ChangeRegionModal from 'components/language-selector-modal';
import Picture from 'components/picture';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import {
  IDeliveryAddress,
  IEditDeliveryAddressInput,
  IsoCountryCode,
} from 'generated/graphql-gateway';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import useErrorModal from 'hooks/use-error-modal';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { CustomEventNames, logNavigationClick } from 'state/amplitude';
import { useLocale } from 'state/intl';
import { primitive } from 'styles/constants/primitives';
import { enableNumericPhoneValidation } from 'utils/environment';
import { FormValidationState } from 'utils/form';
import { getPhoneNumberErrorMessage, isPhoneNumberValid } from 'utils/form';
import logger from 'utils/logger';
import { convertCountryStringToIsoCountryCode2Char } from 'utils/rbi-common';

import {
  INSTRUCTION_SAFE_REGEX,
  MAX_INSTRUCTIONS_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_UNIT_LENGTH,
  PHONE_REGEX,
  SUBMIT_PHONE_REGEX,
  UNIT_REGEX,
} from '../constants';
import { ModalContent } from '../styled';
import {
  ActionStatus,
  AliasLabel,
  DeliveryAddressFieldValidation,
  IDeliveryAddressFormProps,
  IPhoneAddressValidation,
  aliasIcons,
  aliasIconsSize,
  aliasOptions,
  getAliasLabel,
} from '../types';

import { DeliveryAddressFormInput } from './delivery-address-form-input';
import {
  AddressFormContainer,
  AddressInfoLine,
  AliasOptions,
  DeliveryLogoContainer,
  DialogStyles,
} from './styled';

export interface DeliveryAddressFormHandle {
  handleSubmit: () => void;
  hasUnsavedChanges: () => boolean;
}

const isUniqueAliasEnabled = true;
function generateUniqueAlias(baseAlias: string, existingAliases: string[]): string {
  const normalize = (val: string) => val.toLowerCase().replace(/ /g, '');
  const base = normalize(AliasLabel.Other);
  let suffix = 1;

  const matching = existingAliases.filter(alias => normalize(alias).startsWith(base));

  let candidate =
    matching.length === 0
      ? AliasLabel.Other
      : `${AliasLabel.Other}_${String(suffix).padStart(2, '0')}`;
  const normalizedSet = new Set(matching.map(normalize));

  while (normalizedSet.has(normalize(candidate))) {
    suffix++;
    candidate = `${AliasLabel.Other}_${String(suffix).padStart(2, '0')}`;
  }

  return candidate;
}

export const DeliveryAddressForm = forwardRef<
  DeliveryAddressFormHandle,
  PropsWithChildren<IDeliveryAddressFormProps>
>(
  (
    {
      instructions,
      user,
      setCreateAddressView,
      showDeliveryAddresses,
      getDetailsPlaceData,
      setAddress,
      addressToEdit,
      editDeliveryAddress,
      addressComponents,
      setAddressComponents,
      message,
      setMessage,
      clearAddressBeingEdited,
      deliveryAddresses,
      setFormHasError,
      shouldSaveInfoChecked,
      setShouldSaveInfoChecked,
      alias,
      setAlias,
      interceptedClose,
      setInterceptedClose,
      dispatch,
      setShowtermsLink,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const aliasLabels = getAliasLabel(useIntl());
    const [
      pendingRealiasPayload,
      setPendingRealiasPayload,
    ] = useState<IEditDeliveryAddressInput | null>(null);

    const { featureDisclaimers } = useFeatureDisclaimers();
    const { deliveryLogo } = featureDisclaimers || {};
    const { region } = useLocale();
    const unitRef = useRef<HTMLInputElement>(null);
    const deliveryAddressInputRef = useRef<HTMLInputElement>(null);

    const [aliasError, setAliasError] = useState('');

    const [placeId, setPlaceId] = useState('');
    const [addressString, setAddressString] = useState('');
    const [addressString2, setAddressString2] = useState('');
    const [editAddressString, setEditAddressString] = useState('');
    const [ErrorDialog, openErrorDialog] = useErrorModal({
      modalAppearanceEventMessage: 'Delivery Address Form Error',
    });
    const [validation, setValidation] = useState<IPhoneAddressValidation>({
      phoneNumber: FormValidationState.PRISTINE,
      address: FormValidationState.PRISTINE,
    });

    const [coordinates, setCoordinates] = useState<ILocation | undefined>();
    const [deliveryInstructions, setDeliveryInstructions] = useState(instructions);
    const [phoneNumber, setPhoneNumber] = useState(user?.details?.phoneNumber || '');
    const [showChangeRegionModal, setShowChangeRegionModal] = useState<boolean>(false);

    const [phoneNumberError, setPhoneNumberError] = useState('');
    const [instructionsError, setInstructionsError] = useState('');
    const [unitError, setUnitError] = useState('');
    const hasFormError =
      !!message ||
      !!aliasError ||
      !!phoneNumberError ||
      !!unitError ||
      !!instructionsError ||
      !alias;

    const [initialValues, setInitialValues] = useState({
      alias: '',
      phoneNumber: '',
      deliveryInstructions: '',
      addressString: '',
      addressString2: '',
      shouldSaveInfoChecked: false,
    });

    useEffect(() => {
      const defaultPhone = user?.details?.phoneNumber || '';

      if (addressToEdit) {
        setAddressComponents({
          addressLine1: addressToEdit.addressLine1,
          addressLine2: addressToEdit.addressLine2 || '',
          alias: addressToEdit.alias || '',
          city: addressToEdit.city,
          state: addressToEdit.state || '',
          streetNumber: addressToEdit.streetNumber || '',
          route: addressToEdit.route || '',
          zip: addressToEdit.zip || '',
          country: addressToEdit.country || '',
          phoneNumber: addressToEdit.phoneNumber || '',
          defaultInstruction: addressToEdit.defaultInstruction || '',
          latitude: addressToEdit.latitude ?? undefined,
          longitude: addressToEdit.longitude ?? undefined,
        });
        setShouldSaveInfoChecked(true);
        setAddressString(addressToEdit.addressLine1);
        setAddressString2(addressToEdit.addressLine2 || '');
        setDeliveryInstructions(addressToEdit.defaultInstruction || '');
        setEditAddressString(
          `${addressToEdit.addressLine1 || ''}, ${addressToEdit.city || ''}, ${
            addressToEdit.state || ''
          }, ${addressToEdit.country || ''}`
        );
        setAlias(addressToEdit.alias || '');
        setPhoneNumber(addressToEdit.phoneNumber || '');
        setCoordinates(
          addressToEdit.latitude && addressToEdit.longitude
            ? { lat: addressToEdit.latitude, lng: addressToEdit.longitude }
            : undefined
        );
        const initAlias = addressToEdit.alias || '';
        const initPhone = addressToEdit.phoneNumber || '';
        const initInstructions = addressToEdit.defaultInstruction || '';
        const initAddress = addressToEdit.addressLine1;
        const initAddress2 = addressToEdit.addressLine2 || '';

        setInitialValues({
          alias: initAlias,
          phoneNumber: initPhone,
          deliveryInstructions: initInstructions,
          addressString: initAddress,
          addressString2: initAddress2,
          shouldSaveInfoChecked: true,
        });
      } else {
        setAddressComponents(null);
        setAddressString('');
        setAddressString2('');
        setDeliveryInstructions('');

        setAlias('');
        setPhoneNumber(user?.details?.phoneNumber || '');
        setCoordinates(undefined);
        setShouldSaveInfoChecked(false);
        setInitialValues({
          alias: '',
          phoneNumber: defaultPhone,
          deliveryInstructions: '',
          addressString: '',
          addressString2: '',
          shouldSaveInfoChecked: false,
        });
      }
    }, [
      addressToEdit,
      setAddressComponents,
      setAlias,
      setShouldSaveInfoChecked,
      user?.details?.phoneNumber,
    ]);

    useEffect(() => {
      setFormHasError?.(hasFormError);
    }, [hasFormError, setFormHasError]);

    useEffect(() => {
      if (!interceptedClose) {
        return;
      }

      const shouldShowPrompt =
        ref && typeof ref !== 'function' && ref.current?.hasUnsavedChanges?.();

      if (shouldShowPrompt) {
        setShowUnsavedDialog(true);
      } else {
        onExit();
      }
      setTimeout(() => setInterceptedClose(false), 0);
    }, [interceptedClose, ref]);

    useEffect(() => {
      setShowtermsLink(!addressComponents?.addressLine1);
    }, [addressComponents?.addressLine1, setShowtermsLink]);

    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const onExit = () => {
      if (!showDeliveryAddresses) {
        router.dismiss();
      }
      setCreateAddressView(false);
      clearAddressBeingEdited?.();
    };

    const getAddressRegion = useCallback(
      () => convertCountryStringToIsoCountryCode2Char(addressComponents?.country!),
      [addressComponents]
    );

    const changeRegionModalRegionFilter = useCallback(
      (regionForFilter: string) => {
        return getAddressRegion() === regionForFilter;
      },
      [getAddressRegion]
    );

    const handleSubmit = async () => {
      setAliasError('');
      setMessage('');
      if (!addressComponents || validation.address === FormValidationState.INVALID) {
        setMessage(
          logUserErrorMessage({
            message: formatMessage({ id: 'addressError' }),
            severity: LogUserErrorSeverity.Recoverable,
            context: { moreInfo: 'address-invalid' },
          })
        );
        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Invalid Address',
        });
        deliveryAddressInputRef.current?.focus();
        return;
      }

      if (!alias) {
        setAliasError(
          formatMessage({
            id: 'aliasError',
            defaultMessage: 'Please select a label (Home, Work, or Other).',
          })
        );
        return;
      }

      const trimmedPhone = phoneNumber.trim();

      if (!trimmedPhone && !user?.details?.phoneNumber) {
        setPhoneNumberError(
          formatMessage({ id: 'phoneNumberRequired', defaultMessage: 'Phone number is required.' })
        );
        return;
      } else if (
        trimmedPhone &&
        (!SUBMIT_PHONE_REGEX.test(trimmedPhone) || trimmedPhone.length > MAX_PHONE_LENGTH)
      ) {
        setPhoneNumberError(
          formatMessage({
            id: 'invalidPhoneNumberFormat',
            defaultMessage: 'Invalid phone number format. Please enter a valid number.',
          })
        );
        setPhoneNumber(user?.details?.phoneNumber || '');
      } else {
        setPhoneNumberError('');
      }

      dispatch({
        type: ActionStatus.PENDING,
        address: {
          address: addressComponents,
          phoneNumber,
          deliveryInstructions,
          coordinates,
          placeId,
        },
      });

      const trimmedInstructions = deliveryInstructions?.trim() ?? '';

      if (!INSTRUCTION_SAFE_REGEX.test(trimmedInstructions)) {
        setInstructionsError(
          formatMessage({
            id: 'invalidInstructions',
            defaultMessage: 'Instructions contain unsupported characters.',
          })
        );
        return;
      } else if (trimmedInstructions.length > MAX_INSTRUCTIONS_LENGTH) {
        setInstructionsError(
          formatMessage({
            id: 'instructionsTooLong',
            defaultMessage: `Instructions must be less than ${MAX_INSTRUCTIONS_LENGTH} characters.`,
          })
        );
        return;
      }
      setInstructionsError('');

      logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE);

      let lat = addressComponents.latitude;
      let lng = addressComponents.longitude;
      const fullAddress = `${addressComponents.addressLine1}, ${addressComponents.city}, ${addressComponents.state} ${addressComponents.zip}`;

      if (lat === undefined || lng === undefined) {
        try {
          const result = await getDetailsPlaceData(fullAddress);

          lat = result.coordinates.lat;
          lng = result.coordinates.lng;

          setCoordinates(result.coordinates);
          if (addressComponents) {
            setAddressComponents({
              ...addressComponents,
              latitude: lat,
              longitude: lng,
            });
          }
        } catch (err) {
          openErrorDialog({
            message: formatMessage({
              id: 'fetchCoordinatesError',
              defaultMessage:
                'We couldn’t find coordinates for the selected address. Please try again.',
            }),
          });
        }
      }

      if (addressToEdit && editDeliveryAddress) {
        const isAliasChanged =
          addressToEdit.alias?.toLowerCase().replace(/ /g, '') !==
          alias.toLowerCase().replace(/ /g, '');

        const basePayload: IEditDeliveryAddressInput = {
          addressLine1: addressComponents.addressLine1,
          addressLine2: addressComponents.addressLine2,
          alias,
          city: addressComponents.city,
          state: addressComponents.state,
          zip: addressComponents.zip,
          country: addressComponents.country as IsoCountryCode,
          phoneNumber,
          defaultInstruction: deliveryInstructions,
          route: addressComponents.route,
          streetNumber: addressComponents.streetNumber,
          latitude: lat,
          longitude: lng,
        };

        try {
          if (isAliasChanged && pendingRealiasPayload) {
            await editDeliveryAddress(pendingRealiasPayload);
          }
          if (isAliasChanged) {
            const aliasOnlyPayload: IEditDeliveryAddressInput = {
              addressLine1: addressToEdit.addressLine1,
              addressLine2: addressToEdit.addressLine2 || '',
              alias,
              city: addressToEdit.city,
              state: addressToEdit.state || '',
              streetNumber: addressToEdit.streetNumber || '',
              route: addressToEdit.route || '',
              zip: addressToEdit.zip || '',
              country: addressToEdit.country as IsoCountryCode,
              phoneNumber: addressToEdit.phoneNumber || '',
              latitude: addressToEdit.latitude ?? undefined,
              longitude: addressToEdit.longitude ?? undefined,
            };

            await editDeliveryAddress(aliasOnlyPayload);
          }
          await editDeliveryAddress(basePayload);
          dispatch({ type: ActionStatus.SUCCESS });
          setCreateAddressView(false);
          clearAddressBeingEdited?.();
        } catch (err) {
          openErrorDialog({
            message: formatMessage({
              id: 'editAddressFailed',
              defaultMessage: 'Unable to update address. Please try again.',
            }),
          });
        }
      } else {
        try {
          if (pendingRealiasPayload && editDeliveryAddress) {
            await editDeliveryAddress(pendingRealiasPayload);
          }
        } catch (err) {
          openErrorDialog({
            message: formatMessage({
              id: 'editAddressFailed',
              defaultMessage: 'Unable to update address. Please try again.',
            }),
          });
        }
        setPendingRealiasPayload(null);
        setAddress({
          address: {
            ...addressComponents,
            alias,
            defaultInstruction: deliveryInstructions,
            shouldSave: false,
            phoneNumber,
            latitude: lat,
            longitude: lng,
          },
          deliveryInstructions,
          phoneNumber,
          coordinates,
          placeId,
        });
      }
    };

    useImperativeHandle(ref, () => ({
      handleSubmit,
      hasUnsavedChanges: () => {
        return (
          alias !== initialValues.alias ||
          phoneNumber !== initialValues.phoneNumber ||
          deliveryInstructions !== initialValues.deliveryInstructions ||
          addressString !== initialValues.addressString ||
          addressString2 !== initialValues.addressString2 ||
          shouldSaveInfoChecked !== initialValues.shouldSaveInfoChecked
        );
      },
    }));

    const handleSetAddress = useCallback(
      async (placeId: string) => {
        try {
          const placeData = await getDetailsPlaceData(placeId);

          if (!placeData.address.addressLine1) {
            setValidation({ ...validation, address: FormValidationState.INVALID });

            setMessage(
              logUserErrorMessage({
                message: formatMessage({ id: 'addressError' }),

                severity: LogUserErrorSeverity.Recoverable,

                context: { moreInfo: 'address-line-1-missing' },
              })
            );

            return;
          }

          setCoordinates(placeData.coordinates);

          setAddressComponents({
            ...placeData.address,

            phoneNumber,

            shouldSave: false,

            latitude: placeData.coordinates.lat,

            longitude: placeData.coordinates.lng,
          });
        } catch (error) {
          logger.error({ error, message: 'Error using selected address from predictions' });
        }
      },

      [
        formatMessage,
        getDetailsPlaceData,
        phoneNumber,
        setAddressComponents,
        setMessage,
        validation,
      ]
    );

    const handleAliasSelection = (option: string) => {
      const selectedAlias: string = option;

      if (isUniqueAliasEnabled && deliveryAddresses) {
        const isExclusive =
          option === AliasLabel.Home || option === AliasLabel.Work || option === AliasLabel.Other;

        let conflictingAddress: IDeliveryAddress | null = null;
        if (isExclusive) {
          for (const addr of deliveryAddresses) {
            if (!addr.alias) {
              continue;
            }
            if (addr.alias.toLowerCase() === option.toLowerCase()) {
              conflictingAddress = addr;
              break;
            }

            continue;
          }
        }
        if (conflictingAddress) {
          const existingAliases = deliveryAddresses
            .filter(addr => {
              return !(
                addr.addressLine1 === conflictingAddress!.addressLine1 &&
                (addr.addressLine2 || '') === (conflictingAddress!.addressLine2 || '') &&
                addr.city === conflictingAddress!.city &&
                addr.state === conflictingAddress!.state &&
                addr.zip === conflictingAddress!.zip
              );
            })
            .map(addr => addr.alias || '');

          const reassignedAlias = generateUniqueAlias(AliasLabel.Other, existingAliases);

          const payload = {
            addressLine1: conflictingAddress.addressLine1,
            addressLine2: conflictingAddress.addressLine2 || '',
            alias: reassignedAlias,
            city: conflictingAddress.city,
            state: conflictingAddress.state || '',
            zip: conflictingAddress.zip,
            route: conflictingAddress.route || '',
            streetNumber: conflictingAddress.streetNumber || '',
            country: conflictingAddress.country as IsoCountryCode,
            phoneNumber: conflictingAddress.phoneNumber || '',
            defaultInstruction: conflictingAddress.defaultInstruction || '',
            latitude: conflictingAddress.latitude ?? undefined,
            longitude: conflictingAddress.longitude ?? undefined,
          };
          setPendingRealiasPayload(payload);
        } else {
          setPendingRealiasPayload(null);
        }

        const allAliases = deliveryAddresses
          .filter(addr => {
            if (addressToEdit) {
              const isSameAddress =
                addr.addressLine1 === addressToEdit.addressLine1 &&
                (addr.addressLine2 || '') === (addressToEdit.addressLine2 || '') &&
                addr.city === addressToEdit.city &&
                addr.state === addressToEdit.state &&
                addr.zip === addressToEdit.zip;

              return !isSameAddress;
            }
            return true;
          })
          .map(addr => addr.alias)
          .filter((alias): alias is string => !!alias);

        const autoRenamedAlias = generateUniqueAlias(option, allAliases);

        if (autoRenamedAlias !== option) {
          setAliasError(
            formatMessage({
              id: 'aliasAutoRenamed',
              defaultMessage:
                'The selected alias was already used and has been renamed automatically.',
            })
          );
        }
      }
      setAlias(selectedAlias);
      setAliasError('');
    };

    const validateField = (
      field: DeliveryAddressFieldValidation,
      value: string,
      formatMessage: (opts: { id: string; defaultMessage: string }) => string,
      fallback?: string,
      setError?: (msg: string) => void
    ): boolean => {
      if (field === DeliveryAddressFieldValidation.Unit) {
        if (value.length > MAX_UNIT_LENGTH) {
          setError?.('Unit number must be 20 characters or less.');
          return false;
        }
        if (!UNIT_REGEX.test(value)) {
          setError?.('Unit number contains invalid characters.');
          return false;
        }
      }

      if (field === DeliveryAddressFieldValidation.Phone) {
        if (!value && !fallback) {
          setError?.(
            formatMessage({
              id: 'phoneNumberRequired',
              defaultMessage: 'Phone number is required.',
            })
          );
          return false;
        }

        if (value && (!PHONE_REGEX.test(value) || value.length > MAX_PHONE_LENGTH)) {
          setError?.(
            formatMessage({
              id: 'invalidPhoneNumberFormat',
              defaultMessage: 'Invalid phone number format. Please enter a valid number.',
            })
          );
          return false;
        }
      }

      if (field === DeliveryAddressFieldValidation.Instructions) {
        if (!INSTRUCTION_SAFE_REGEX.test(value)) {
          setError?.(
            formatMessage({
              id: 'invalidInstructions',
              defaultMessage: 'Instructions contain unsupported characters.',
            })
          );
          return false;
        }
        if (value.length > MAX_INSTRUCTIONS_LENGTH) {
          setError?.(
            formatMessage({
              id: 'instructionsTooLong',
              defaultMessage: `Instructions must be less than ${MAX_INSTRUCTIONS_LENGTH} characters.`,
            })
          );
          return false;
        }
      }

      setError?.('');
      return true;
    };

    return (
      <>
        <ModalContent>
          {Platform.OS === 'android' && !addressComponents?.addressLine1 && (
            <>
              <View
                style={{
                  marginVertical: 16,
                  alignItems: 'center',
                  width: '100%',
                  height: 200,
                  left: '3%',
                  top: 7,
                  backgroundColor: 'white',
                  position: 'absolute',
                  borderRadius: 12,
                }}
              ></View>
            </>
          )}

          {!addressComponents?.addressLine1 ? (
            <AddressFormContainer
              style={{
                ...(Platform.OS === 'android' ? { height: 500 } : { backgroundColor: 'white' }),
              }}
              testID="delivery-address-form"
            >
              <HStack alignItems="center">
                <AddressInfoLine>
                  {formatMessage({
                    id: 'addressRequired',
                    defaultMessage: 'Address (Required)',
                  })}
                </AddressInfoLine>
                <Text color={primitive.bk.$alertRed} mt="$4">
                  *
                </Text>
              </HStack>

              <DeliveryAddressFormInput
                onHandleSetAddress={handleSetAddress}
                setAddressString={setAddressString}
                setPlaceId={setPlaceId}
                setValidation={setValidation}
                setMessage={setMessage}
                setAddressComponents={setAddressComponents}
                unitRef={unitRef}
                validation={validation}
                setCoordinates={setCoordinates}
                addressString={addressToEdit ? editAddressString : addressString}
                setValue={setAddressString}
                message={message}
                deliveryAddressInputRef={deliveryAddressInputRef}
                data-private
                aria-invalid={!!message}
                isAddressLocked={!!addressToEdit}
              />
              <View style={{ marginVertical: 16, alignItems: 'center' }}>
                <Text color={primitive.bk.$bbqBrown} fontSize={16} style={{ textAlign: 'center' }}>
                  {formatMessage({
                    id: 'NewAddressAdding',
                    defaultMessage: 'Unit can be entered in next step',
                  })}
                </Text>
              </View>
            </AddressFormContainer>
          ) : (
            <AddressFormContainer
              style={{ backgroundColor: primitive.bk.$white }}
              testID="delivery-address-form"
            >
              <HStack alignItems="center">
                <AddressInfoLine>
                  {formatMessage({
                    id: 'addressRequired',
                    defaultMessage: 'Address (Required)',
                  })}
                </AddressInfoLine>
                <Text color={primitive.bk.$alertRed} mt="$4">
                  *
                </Text>
              </HStack>

              <DeliveryAddressFormInput
                onHandleSetAddress={handleSetAddress}
                setAddressString={setAddressString}
                setPlaceId={setPlaceId}
                setValidation={setValidation}
                setMessage={setMessage}
                setAddressComponents={setAddressComponents}
                unitRef={unitRef}
                validation={validation}
                setCoordinates={setCoordinates}
                addressString={addressToEdit ? editAddressString : addressString}
                setValue={setAddressString}
                message={message}
                deliveryAddressInputRef={deliveryAddressInputRef}
                data-private
                aria-invalid={!!message}
                isAddressLocked={!!addressToEdit}
              />
              <AddressInfoLine>
                {formatMessage({
                  id: 'unitNumberInput',
                  defaultMessage: 'Unit',
                })}
              </AddressInfoLine>
              <Input
                testID="unit-number"
                value={addressComponents?.addressLine2 || ''}
                onChangeText={text => {
                  validateField(
                    DeliveryAddressFieldValidation.Unit,
                    text,
                    formatMessage,
                    undefined,
                    setUnitError
                  );
                  if (text.length > MAX_UNIT_LENGTH) {
                    return;
                  }
                  if (addressComponents) {
                    setAddressComponents({ ...addressComponents, addressLine2: text });
                  }
                  setAddressString2(text);
                }}
                py="$4"
                borderColor={unitError ? primitive.bk.$bbqRed : undefined}
              />
              {!!unitError && (
                <Text color={primitive.bk.$bbqRed} fontSize="sm" mt="$1">
                  {unitError}
                </Text>
              )}

              <HStack alignItems="center">
                <AddressInfoLine>
                  {formatMessage({
                    id: 'pickALabel',
                    defaultMessage: 'Pick a Label (Required)',
                  })}
                </AddressInfoLine>
                <Text color={primitive.bk.$alertRed} mt="$4">
                  *
                </Text>
              </HStack>
              <HStack flexWrap="wrap" space="$1" justifyContent="flex-start">
                {aliasOptions.map(option => {
                  const isSelected = alias.startsWith(option);
                  const displayAlias = aliasLabels[option];

                  return (
                    <Pressable
                      key={option}
                      onPress={() => {
                        handleAliasSelection(option);
                      }}
                      testID={`alias-${option}`}
                    >
                      <Box
                        px="$4"
                        py="$1"
                        marginBottom={1}
                        borderRadius="26"
                        borderWidth={1}
                        borderColor={aliasError ? primitive.bk.$bbqRed : primitive.bk.$bbqBrown}
                        backgroundColor={isSelected ? primitive.bk.$bbqBrown : 'transparent'}
                      >
                        <HStack space="$1" alignItems="center">
                          <Icon
                            variant={aliasIcons[option]}
                            size={aliasIconsSize[option]}
                            color={isSelected ? primitive.bk.$white : primitive.bk.$bbqBrown}
                          />
                          <AliasOptions color={isSelected ? '#FFFFFF' : primitive.bk.$bbqBrown}>
                            {displayAlias}
                          </AliasOptions>
                        </HStack>
                      </Box>
                    </Pressable>
                  );
                })}
              </HStack>
              {!!aliasError && (
                <Text color={primitive.bk.$bbqRed} fontSize="sm" mt="$1">
                  {aliasError}
                </Text>
              )}

              <HStack alignItems="center">
                <AddressInfoLine>
                  {formatMessage({
                    id: 'phoneNumberRequired',
                    defaultMessage: 'Phone Number (Required)',
                  })}
                </AddressInfoLine>
                <Text color={primitive.bk.$alertRed} mt="$4">
                  *
                </Text>
              </HStack>

              <Input
                testID="phone-number"
                placeholder="+13050000000"
                placeholderTextColor={primitive.bk.$blackOpacity50}
                value={phoneNumber}
                onChangeText={async text => {
                  const trimmed = text.replace(/[^+\d]/g, '');
                  setPhoneNumber(trimmed);

                  const { valid, error } = await isPhoneNumberValid({
                    phoneNumber: trimmed,
                    simpleValidation: enableNumericPhoneValidation(),
                    country: region,
                  });

                  if (!valid) {
                    setPhoneNumberError(getPhoneNumberErrorMessage(error, formatMessage));
                    setValidation(prev => ({
                      ...prev,
                      phoneNumber: FormValidationState.INVALID,
                    }));
                  } else {
                    setPhoneNumberError('');
                    setValidation(prev => ({
                      ...prev,
                      phoneNumber: FormValidationState.VALID,
                    }));
                  }
                }}
                py="$4"
                borderColor={
                  validation.phoneNumber === FormValidationState.INVALID
                    ? primitive.bk.$bbqRed
                    : undefined
                }
              />
              {!!phoneNumberError && (
                <Text color={primitive.bk.$bbqRed} fontSize="sm" mt="$1">
                  {phoneNumberError}
                </Text>
              )}

              <AddressInfoLine>
                {formatMessage({
                  id: 'deliveryInstructions',
                  defaultMessage: 'Delivery Instructions',
                })}
              </AddressInfoLine>
              <Input
                testID="instructions"
                placeholder={formatMessage({
                  id: 'instructionsPlaceholder',
                  defaultMessage: 'Tell the driver where to leave your order',
                })}
                placeholderTextColor={primitive.bk.$blackOpacity50}
                value={deliveryInstructions}
                onChangeText={text => {
                  validateField(
                    DeliveryAddressFieldValidation.Instructions,
                    text,
                    formatMessage,
                    undefined,
                    setInstructionsError
                  );
                  if (text.length > MAX_INSTRUCTIONS_LENGTH) {
                    return;
                  }
                  setDeliveryInstructions(text);
                }}
                py="$4"
                borderColor={instructionsError ? primitive.bk.$bbqRed : undefined}
              />
              {!!instructionsError && (
                <Text color={primitive.bk.$bbqRed} fontSize="sm" mt="$1">
                  {instructionsError}
                </Text>
              )}
              <Checkbox
                isChecked={!!addressToEdit || shouldSaveInfoChecked}
                isDisabled={!!addressToEdit}
                onChange={!addressToEdit ? checked => setShouldSaveInfoChecked(checked) : undefined}
                testID="save-info-checkbox"
                mb="$4"
                mt="$4"
                value=""
              >
                {formatMessage({
                  id: 'deliveryShouldSaveInfoCheckboxMessage',
                  defaultMessage:
                    'Save my information for updates on order status and driver communication. Your number will not be visible to the driver.',
                })}
              </Checkbox>
            </AddressFormContainer>
          )}
          {!!message && (
            <VisuallyHidden
              role="alert"
              accessibilityLabel={formatMessage({ id: 'thereIsAnErrorInThisForm' })}
            />
          )}
          {deliveryLogo?.locale && (
            <DeliveryLogoContainer>
              <Picture
                image={deliveryLogo?.locale}
                alt={formatMessage({ id: 'deliveryLogoAlt' })}
              />
            </DeliveryLogoContainer>
          )}
        </ModalContent>
        <ErrorDialog />
        {showChangeRegionModal && (
          <ChangeRegionModal
            redirectToCurrentLocation
            heading={formatMessage({ id: 'deliveryChangeRegionHeading' })}
            regionFilter={changeRegionModalRegionFilter}
            onModalDismiss={() => setShowChangeRegionModal(false)}
            primaryText={formatMessage(
              { id: 'deliveryChangeRegionPrimaryText' },
              { userRegion: region, restaurantRegion: getAddressRegion() }
            )}
            secondaryText={formatMessage({ id: 'locatorChangeRegionSelection' })}
            disclaimer={formatMessage({ id: 'deliveryChangeRegionDisclaimer' })}
          />
        )}
        {showUnsavedDialog && (
          <Dialog
            showDialog={showUnsavedDialog}
            headingComponent={
              <View style={DialogStyles.headingContainer}>
                <Text variant="headerOne" textAlign="center">
                  {formatMessage({
                    id: 'unsavedChangesHeading',
                    defaultMessage: 'Unsaved Changes',
                  })}
                </Text>
              </View>
            }
            bodyComponent={
              <View style={DialogStyles.bodyContainer}>
                <Text variant="body" textAlign="center">
                  {formatMessage({
                    id: 'unsavedChangesBody',
                    defaultMessage:
                      'You’ve made changes to this address. Do you want to save them before leaving?',
                  })}
                </Text>
              </View>
            }
            confirmButtonLabel={formatMessage({
              id: 'continueEditing ',
              defaultMessage: 'Continue Editing',
            })}
            confirmDialog={() => setShowUnsavedDialog(false)}
            showCloseButton
            closeButtonVariant={ActionButtonVariants.TEXT_ONLY}
            closeButtonLabelId={formatMessage({
              id: 'Discard',
              defaultMessage: 'Discard',
            })}
            onDismiss={() => {
              setShowUnsavedDialog(false);
              onExit();
            }}
            modalAppearanceEventMessage={formatMessage({
              id: 'exitDeliveryAddressMessage',
              defaultMessage: 'Exit Delivery Address With Unsaved Changes',
            })}
          />
        )}
      </>
    );
  }
);
